/**
 * Phase 9 — Winhouse Collaboration Hub (shared UI).
 *
 * A single component used by student/tutor/admin portals. Layout:
 *   Rooms list (left) → Channels list + message stream (centre) → composer
 * Homework panel is a modal.
 *
 * Reused by:
 *   /portal/StudentDashboardPage.jsx  (student)
 *   /portal/TutorDashboardPage.jsx    (tutor)
 *   /AdminDashboardV5.jsx             (admin — full moderation controls)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MessageCircle, Send, Paperclip, Pin, PinOff, Search, Users, Archive,
  Loader2, ClipboardCheck, Plus, X, Trash2, Smile, RefreshCw, Trophy, Bell,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: BACKEND_URL, withCredentials: true });

const REACT_SET = ['👍', '👏', '🎉', '❤️', '🔥'];

const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch (_e) { return iso; } };
const fmtShort = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }); } catch (_e) { return iso; } };

/* ---------- Rooms list ---------- */
const RoomRow = ({ r, active, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`hub-room-${r.room_id}`}
    className={`w-full text-left p-3 rounded-xl border transition-colors ${active ? 'border-red-600 bg-red-50' : 'border-gray-200 hover:border-red-300 hover:bg-gray-50'}`}>
    <div className="flex items-center justify-between gap-2">
      <div className="text-sm font-bold text-gray-900 truncate">{r.student_name}</div>
      {r.unread_count > 0 && (
        <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-700 text-white" data-testid={`hub-unread-${r.room_id}`}>{r.unread_count}</span>
      )}
    </div>
    <div className="text-[10px] text-gray-500 truncate">{r.program} · {r.tutor_emails?.length || 0} tutor(s)</div>
    <div className="text-[9px] text-gray-400 mt-0.5">{r.message_count} msgs</div>
  </button>
);

/* ---------- Message bubble ---------- */
const RoleBadge = ({ role }) => {
  if (!role) return null;
  const map = {
    student: 'bg-sky-100 text-sky-800',
    tutor: 'bg-emerald-100 text-emerald-800',
    admin: 'bg-rose-100 text-rose-800',
    owner: 'bg-rose-100 text-rose-800',
    parent: 'bg-amber-100 text-amber-800',
    system: 'bg-gray-100 text-gray-700',
  };
  return <span className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full ${map[role] || 'bg-gray-100 text-gray-700'}`}>{role}</span>;
};

const Message = ({ m, canPin, canDelete, onReact, onPin, onDelete }) => {
  const isAnnouncement = m.kind === 'announcement';
  return (
    <div className={`group rounded-xl p-3 border ${m.pinned ? 'border-amber-300 bg-amber-50' : isAnnouncement ? 'border-red-200 bg-red-50' : 'border-gray-100 bg-white'}`}
      data-testid={`hub-msg-${m.message_id}`}>
      <div className="flex items-center gap-2 text-[11px] text-gray-500 mb-1">
        <div className="font-bold text-gray-900">{m.sender_name || m.sender_email}</div>
        <RoleBadge role={m.sender_role} />
        <span className="text-gray-400">·</span>
        <span title={fmt(m.created_at)}>{fmtShort(m.created_at)}</span>
        {m.pinned && <Pin className="h-3 w-3 text-amber-600 ml-auto" />}
      </div>
      {m.body && <div className="text-sm text-gray-900 whitespace-pre-line">{m.body}</div>}
      {m.attachments?.length > 0 && (
        <div className="mt-2 space-y-1">
          {m.attachments.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-xs text-red-700 hover:underline mr-3" data-testid={`hub-attach-${m.message_id}-${i}`}>
              <Paperclip className="h-3 w-3" /> {a.filename || 'file'}
            </a>
          ))}
        </div>
      )}
      {m.kind === 'homework_assign' && m.meta?.homework_id && (
        <div className="mt-2 text-[10px] uppercase tracking-wider font-bold text-red-700">Homework · {m.meta?.due_date ? `Due ${fmt(m.meta.due_date)}` : 'No due date'}</div>
      )}
      {/* Reactions */}
      {Object.keys(m.reactions || {}).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {Object.entries(m.reactions).map(([emoji, users]) => (
            <button key={emoji} onClick={() => onReact(m, emoji)}
              className="text-xs px-2 py-0.5 rounded-full bg-white border border-gray-200 hover:border-red-500">
              {emoji} {users.length}
            </button>
          ))}
        </div>
      )}
      {/* Action row */}
      <div className="mt-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-[10px]">
        <div className="inline-flex items-center gap-0.5">
          {REACT_SET.map(e => (
            <button key={e} onClick={() => onReact(m, e)}
              className="hover:bg-gray-100 rounded p-1 text-sm" data-testid={`hub-react-${m.message_id}-${e}`}>{e}</button>
          ))}
        </div>
        {canPin && <button onClick={() => onPin(m)} className="text-gray-500 hover:text-amber-700 inline-flex items-center gap-0.5"
          data-testid={`hub-pin-${m.message_id}`}>{m.pinned ? <><PinOff className="h-3 w-3" /> Unpin</> : <><Pin className="h-3 w-3" /> Pin</>}</button>}
        {canDelete && <button onClick={() => onDelete(m)} className="text-gray-500 hover:text-rose-700 inline-flex items-center gap-0.5"
          data-testid={`hub-delete-${m.message_id}`}><Trash2 className="h-3 w-3" /> Delete</button>}
      </div>
    </div>
  );
};

/* ---------- Composer ---------- */
const Composer = ({ onSend }) => {
  const [text, setText] = useState('');
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!text.trim() && !url.trim()) return;
    setBusy(true);
    const atts = url.trim() ? [{ url, filename: name || url.split('/').pop() }] : [];
    try {
      await onSend(text, atts);
      setText(''); setUrl(''); setName('');
    } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-gray-200 p-3 space-y-2 bg-white" data-testid="hub-composer">
      <textarea value={text} onChange={e => setText(e.target.value)}
        rows={2} placeholder="Write a message…" data-testid="hub-composer-text"
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) send(); }}
        className="w-full text-sm border rounded-lg px-3 py-2 focus:outline-none focus:border-black resize-none" />
      <div className="flex items-center gap-2 flex-wrap">
        <input value={url} onChange={e => setUrl(e.target.value)} placeholder="Attachment URL (optional)"
          className="flex-1 text-xs border rounded-lg px-3 py-1.5" data-testid="hub-composer-url" />
        <input value={name} onChange={e => setName(e.target.value)} placeholder="filename"
          className="w-40 text-xs border rounded-lg px-3 py-1.5" data-testid="hub-composer-filename" />
        <button onClick={send} disabled={busy}
          data-testid="hub-composer-send"
          className="inline-flex items-center gap-1 h-9 px-4 rounded-full bg-red-700 hover:bg-red-800 text-white text-xs font-bold uppercase tracking-wider disabled:opacity-40">
          {busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />} Send
        </button>
      </div>
    </div>
  );
};

/* ---------- Homework panel ---------- */
const HomeworkPanel = ({ roomId, myRole, myEmail, onClose }) => {
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({});
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [due, setDue] = useState('');
  const canAssign = myRole === 'tutor' || myRole === 'admin' || myRole === 'owner';
  const load = useCallback(async () => {
    try {
      const r = await ax.get(`/api/me/collab/rooms/${roomId}/homework`);
      setItems(r.data.items || []); setTotals(r.data.totals || {});
    } catch (_e) { /* silent */ }
  }, [roomId]);
  useEffect(() => { load(); }, [load]);
  const assign = async () => {
    if (!title.trim()) return;
    try {
      await ax.post(`/api/me/collab/rooms/${roomId}/homework`, { title, body, due_date: due || undefined });
      toast.success('Homework assigned');
      setTitle(''); setBody(''); setDue(''); setCreating(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Assign failed'); }
  };
  const submit = async (h) => {
    const bod = window.prompt('Submission note (optional):') || '';
    try { await ax.post(`/api/me/collab/homework/${h.homework_id}/submit`, { body: bod }); toast.success('Submitted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Submit failed'); }
  };
  const review = async (h) => {
    const note = window.prompt('Review feedback:') || '';
    try { await ax.post(`/api/me/collab/homework/${h.homework_id}/review`, { review_note: note, completed: true }); toast.success('Reviewed'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Review failed'); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="hub-homework-panel">
      <div className="bg-white rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-white z-10 flex items-center justify-between p-4 border-b">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-red-700 font-bold">Homework</div>
            <div className="text-sm font-bold">Room · {roomId.slice(0, 14)}…</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full" data-testid="hub-homework-close"><X className="h-4 w-4" /></button>
        </div>
        <div className="p-4 space-y-3">
          {canAssign && (
            <>
              <button onClick={() => setCreating(c => !c)}
                data-testid="hub-homework-assign-toggle"
                className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full bg-black text-[#D3D86F] hover:bg-gray-900">
                <Plus className="h-3 w-3" /> Assign new
              </button>
              {creating && (
                <div className="border border-gray-200 rounded-xl p-3 space-y-2">
                  <input value={title} onChange={e => setTitle(e.target.value)} placeholder="Homework title" data-testid="hub-hw-title" className="w-full text-sm border rounded-lg px-3 py-2" />
                  <textarea value={body} onChange={e => setBody(e.target.value)} rows={3} placeholder="Instructions for the student…" data-testid="hub-hw-body" className="w-full text-sm border rounded-lg px-3 py-2" />
                  <input value={due} onChange={e => setDue(e.target.value)} placeholder="Due date (YYYY-MM-DD or ISO)" data-testid="hub-hw-due" className="w-full text-xs border rounded-lg px-3 py-2" />
                  <button onClick={assign}
                    data-testid="hub-hw-assign-btn"
                    className="inline-flex items-center gap-1 text-[11px] font-bold px-3 py-1.5 rounded-full bg-red-700 hover:bg-red-800 text-white">
                    <Send className="h-3 w-3" /> Assign
                  </button>
                </div>
              )}
            </>
          )}
          <div className="grid grid-cols-5 gap-2 text-center text-[10px] uppercase tracking-wider font-bold" data-testid="hub-hw-totals">
            {[['assigned', 'sky'], ['submitted', 'amber'], ['reviewed', 'purple'], ['completed', 'emerald'], ['late', 'rose']].map(([k, tone]) => (
              <div key={k} className={`rounded-lg p-2 bg-${tone}-50 text-${tone}-800`}>{k} · {totals[k] || 0}</div>
            ))}
          </div>
          <div className="space-y-2">
            {items.length === 0 && <div className="text-xs italic text-gray-400 p-3 text-center">No homework yet.</div>}
            {items.map(h => (
              <div key={h.homework_id} className="border border-gray-200 rounded-xl p-3" data-testid={`hub-hw-${h.homework_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-sm font-bold">{h.title}</div>
                    <div className="text-[11px] text-gray-500">By {h.assigned_by_name} · {h.due_date ? `Due ${h.due_date}` : 'No due date'}</div>
                  </div>
                  <span className={`text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${h.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : h.status === 'late' ? 'bg-rose-100 text-rose-800' : h.status === 'submitted' ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`}>{h.status}</span>
                </div>
                {h.body && <p className="text-xs text-gray-700 mt-2 whitespace-pre-line">{h.body}</p>}
                {h.submission_body && (
                  <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 p-2 text-xs">
                    <div className="font-bold text-gray-700 mb-1">Student submission</div>
                    <div className="whitespace-pre-line">{h.submission_body}</div>
                  </div>
                )}
                {h.review_note && (
                  <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 p-2 text-xs">
                    <div className="font-bold text-emerald-800 mb-1">Tutor review</div>
                    <div className="whitespace-pre-line">{h.review_note}</div>
                  </div>
                )}
                <div className="mt-2 flex gap-2">
                  {myRole === 'student' && h.student_email === myEmail && (h.status === 'assigned' || h.status === 'late') && (
                    <button onClick={() => submit(h)} data-testid={`hub-hw-submit-${h.homework_id}`}
                      className="text-[10px] font-bold px-3 h-8 rounded-full bg-red-700 hover:bg-red-800 text-white uppercase tracking-wider">Submit</button>
                  )}
                  {canAssign && (h.status === 'submitted' || h.status === 'late') && (
                    <button onClick={() => review(h)} data-testid={`hub-hw-review-${h.homework_id}`}
                      className="text-[10px] font-bold px-3 h-8 rounded-full bg-emerald-700 hover:bg-emerald-800 text-white uppercase tracking-wider">Review & complete</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ---------- Main hub ---------- */
const CollaborationHub = ({ isAdmin = false }) => {
  const [me, setMe] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [roomId, setRoomId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [channelId, setChannelId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [homeworkOpen, setHomeworkOpen] = useState(false);
  const scroller = useRef(null);
  const pollTimer = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const [meR, rr] = await Promise.all([
          ax.get('/api/auth/me'),
          ax.get(isAdmin ? '/api/admin/collab/rooms' : '/api/me/collab/rooms'),
        ]);
        setMe(meR.data);
        setRooms(rr.data.items || []);
        if ((rr.data.items || []).length > 0) setRoomId(rr.data.items[0].room_id);
      } catch (_e) { toast.error('Could not load hub'); }
    })();
  }, [isAdmin]);

  const loadDetail = useCallback(async () => {
    if (!roomId) { setDetail(null); return; }
    try {
      const r = await ax.get(`/api/me/collab/rooms/${roomId}`);
      setDetail(r.data);
      if (!channelId && (r.data.channels || []).length > 0) setChannelId(r.data.channels[0].channel_id);
    } catch (_e) { /* silent */ }
  }, [roomId, channelId]);
  useEffect(() => { loadDetail(); }, [loadDetail]);

  const loadMessages = useCallback(async () => {
    if (!roomId || !channelId) return;
    setLoadingMsgs(true);
    try {
      const r = await ax.get(`/api/me/collab/rooms/${roomId}/channels/${channelId}/messages`);
      setMessages(r.data.items || []);
      // Mark read
      await ax.post(`/api/me/collab/rooms/${roomId}/channels/${channelId}/read`);
      // Re-fetch rooms to refresh unread counts
      const rr = await ax.get(isAdmin ? '/api/admin/collab/rooms' : '/api/me/collab/rooms');
      setRooms(rr.data.items || []);
      // Scroll to bottom
      setTimeout(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, 100);
    } catch (_e) { /* silent */ }
    finally { setLoadingMsgs(false); }
  }, [roomId, channelId, isAdmin]);
  useEffect(() => { loadMessages(); }, [loadMessages]);

  // Poll every 10s for new messages in the active channel
  useEffect(() => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    if (!roomId || !channelId) return;
    pollTimer.current = setInterval(loadMessages, 12000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [roomId, channelId, loadMessages]);

  const activeChannel = useMemo(
    () => (detail?.channels || []).find(c => c.channel_id === channelId),
    [detail, channelId]);

  const myRole = (me?.role || '').toLowerCase();
  const canPin = myRole === 'tutor' || myRole === 'admin' || myRole === 'owner';
  const canDelete = myRole === 'admin' || myRole === 'owner';

  const send = async (text, attachments) => {
    if (!roomId || !channelId) return;
    try {
      await ax.post(`/api/me/collab/rooms/${roomId}/channels/${channelId}/messages`,
        { body: text, kind: 'text', attachments });
      await loadMessages();
    } catch (e) { toast.error(e.response?.data?.detail || 'Send failed'); throw e; }
  };
  const react = async (m, emoji) => {
    try { await ax.post(`/api/me/collab/messages/${m.message_id}/react`, { emoji }); await loadMessages(); }
    catch (_e) { toast.error('Reaction failed'); }
  };
  const pin = async (m) => {
    try { await ax.post(`/api/me/collab/messages/${m.message_id}/pin`, { pinned: !m.pinned }); await loadMessages(); }
    catch (_e) { toast.error('Pin failed'); }
  };
  const remove = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try { await ax.post(`/api/me/collab/messages/${m.message_id}/delete`, {}); await loadMessages(); }
    catch (_e) { toast.error('Delete failed'); }
  };

  const runSearch = async () => {
    if (!search.trim()) { setSearchResults(null); return; }
    try {
      const r = await ax.get(`/api/me/collab/search?q=${encodeURIComponent(search)}`);
      setSearchResults(r.data.items || []);
    } catch (_e) { toast.error('Search failed'); }
  };

  const archiveRoom = async () => {
    if (!isAdmin || !roomId) return;
    if (!window.confirm('Archive this room?')) return;
    try {
      await ax.post(`/api/admin/collab/rooms/${roomId}/archive`, { archived: !detail?.room?.archived });
      toast.success('Archive toggled');
      const rr = await ax.get('/api/admin/collab/rooms');
      setRooms(rr.data.items || []);
    } catch (_e) { toast.error('Archive failed'); }
  };

  if (!me) return <div className="p-6"><Loader2 className="h-5 w-5 animate-spin text-red-700" /></div>;

  return (
    <div className="grid lg:grid-cols-[280px_1fr_240px] gap-3 h-[75vh] min-h-[600px]" data-testid="collab-hub">
      {/* LEFT — rooms */}
      <aside className="bg-white border border-gray-200 rounded-2xl flex flex-col overflow-hidden">
        <div className="p-3 border-b border-gray-100 flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-red-700" />
          <div className="text-sm font-bold">Rooms</div>
          <span className="ml-auto text-[10px] text-gray-500" data-testid="hub-rooms-total">{rooms.length}</span>
        </div>
        <div className="p-2 border-b border-gray-100">
          <div className="relative">
            <Search className="h-3 w-3 absolute left-2 top-2 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') runSearch(); }}
              placeholder="Search messages…" data-testid="hub-search-input"
              className="w-full text-xs pl-6 pr-2 py-1.5 rounded-full border border-gray-200 focus:outline-none focus:border-black" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-2 space-y-1.5">
          {rooms.map(r => <RoomRow key={r.room_id} r={r} active={r.room_id === roomId} onClick={() => { setRoomId(r.room_id); setChannelId(null); }} />)}
          {rooms.length === 0 && <div className="text-xs italic text-gray-400 p-3 text-center">No rooms yet.</div>}
        </div>
      </aside>

      {/* CENTRE — messages */}
      <section className="bg-white border border-gray-200 rounded-2xl flex flex-col overflow-hidden">
        {searchResults ? (
          <div className="p-4 flex-1 overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Search · "{search}" · {searchResults.length}</div>
              <button onClick={() => { setSearchResults(null); setSearch(''); }} className="text-xs text-red-700 hover:underline">Clear</button>
            </div>
            <div className="space-y-2">
              {searchResults.map(m => (
                <div key={m.message_id} className="border border-gray-100 rounded-xl p-2 text-xs" data-testid={`hub-search-hit-${m.message_id}`}>
                  <div className="text-gray-500">{m.sender_name} · {fmt(m.created_at)}</div>
                  <div className="text-gray-900">{m.body}</div>
                </div>
              ))}
              {searchResults.length === 0 && <div className="text-xs italic text-gray-400">No matches.</div>}
            </div>
          </div>
        ) : detail?.room ? (
          <>
            <div className="p-3 border-b border-gray-100">
              <div className="flex items-center gap-2 flex-wrap">
                <Users className="h-4 w-4 text-red-700" />
                <div>
                  <div className="text-sm font-bold">{detail.room.student_name}</div>
                  <div className="text-[10px] text-gray-500">{detail.room.program} · {(detail.room.tutor_emails || []).length} tutor(s)</div>
                </div>
                <div className="ml-auto flex items-center gap-1">
                  <button onClick={() => setHomeworkOpen(true)} className="text-[10px] px-2 h-7 rounded-full bg-black text-[#D3D86F] hover:bg-gray-900 font-bold inline-flex items-center gap-1" data-testid="hub-open-homework">
                    <ClipboardCheck className="h-3 w-3" /> Homework {detail.homework_pending > 0 && <span className="bg-red-600 text-white px-1 rounded-full text-[9px]">{detail.homework_pending}</span>}
                  </button>
                  {isAdmin && (
                    <button onClick={archiveRoom} data-testid="hub-archive-btn" className="text-[10px] px-2 h-7 rounded-full bg-white border border-gray-200 hover:border-black text-gray-800 font-bold inline-flex items-center gap-1">
                      <Archive className="h-3 w-3" /> {detail.room.archived ? 'Unarchive' : 'Archive'}
                    </button>
                  )}
                  <button onClick={loadMessages} className="p-1 hover:bg-gray-100 rounded-full" title="Refresh"><RefreshCw className="h-3 w-3 text-gray-500" /></button>
                </div>
              </div>
              {/* Channel tabs */}
              <div className="mt-2 flex flex-wrap gap-1" data-testid="hub-channels">
                {(detail.channels || []).map(c => (
                  <button key={c.channel_id} onClick={() => setChannelId(c.channel_id)}
                    data-testid={`hub-channel-${c.key}`}
                    className={`text-[11px] px-3 h-7 rounded-full font-bold uppercase tracking-wider ${c.channel_id === channelId ? 'bg-red-700 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                    # {c.name}
                  </button>
                ))}
              </div>
            </div>
            {/* Message list */}
            <div ref={scroller} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50" data-testid="hub-messages">
              {loadingMsgs && messages.length === 0 && <div className="text-center text-xs text-gray-400 p-4"><Loader2 className="h-4 w-4 animate-spin inline" /></div>}
              {messages.length === 0 && !loadingMsgs && <div className="text-xs italic text-gray-400 text-center py-8">No messages yet — start the conversation!</div>}
              {messages.map(m => (
                <Message key={m.message_id} m={m}
                  canPin={canPin} canDelete={canDelete || (m.sender_email === (me?.email || '').toLowerCase())}
                  onReact={react} onPin={pin} onDelete={remove} />
              ))}
            </div>
            {/* Composer */}
            {activeChannel && (
              activeChannel.key === 'tutor_notes' && myRole === 'student' ? (
                <div className="p-3 text-xs text-gray-500 border-t border-gray-100 italic text-center">This channel is visible to tutors and admin only.</div>
              ) : <Composer onSend={send} />
            )}
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-xs italic text-gray-400">
            {rooms.length > 0 ? 'Select a room to open its channels' : 'You have no collaboration rooms yet.'}
          </div>
        )}
      </section>

      {/* RIGHT — pinned + members */}
      <aside className="bg-white border border-gray-200 rounded-2xl flex flex-col overflow-hidden">
        <div className="p-3 border-b border-gray-100 flex items-center gap-2">
          <Pin className="h-4 w-4 text-amber-600" />
          <div className="text-sm font-bold">Pinned & Members</div>
        </div>
        <div className="p-3 space-y-3 overflow-y-auto text-xs">
          {detail?.room && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Members</div>
              <ul className="space-y-1 break-all" data-testid="hub-members">
                <li className="flex items-center gap-1.5 min-w-0"><RoleBadge role="student" /> <span className="truncate">{detail.room.student_name}</span></li>
                {(detail.room.tutor_emails || []).map(t => <li key={t} className="flex items-center gap-1.5 min-w-0"><RoleBadge role="tutor" /> <span className="truncate">{t}</span></li>)}
                {detail.room.parent_email && <li className="flex items-center gap-1.5 min-w-0"><RoleBadge role="parent" /> <span className="truncate">{detail.room.parent_email}</span></li>}
                {(detail.room.admin_emails || []).map(a => <li key={a} className="flex items-center gap-1.5 min-w-0"><RoleBadge role="admin" /> <span className="truncate">{a}</span></li>)}
              </ul>
            </div>
          )}
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Pinned</div>
            <ul className="space-y-1.5" data-testid="hub-pinned">
              {messages.filter(m => m.pinned).slice(0, 6).map(m => (
                <li key={m.message_id} className="text-[11px] rounded-lg border border-amber-100 bg-amber-50 p-2">
                  <div className="font-bold text-amber-800">{m.sender_name}</div>
                  <div className="text-gray-800 line-clamp-3">{m.body}</div>
                </li>
              ))}
              {messages.filter(m => m.pinned).length === 0 && <li className="text-[11px] italic text-gray-400">No pins yet.</li>}
            </ul>
          </div>
        </div>
      </aside>

      {homeworkOpen && detail?.room && (
        <HomeworkPanel roomId={detail.room.room_id} myRole={myRole} myEmail={(me?.email || '').toLowerCase()}
          onClose={() => { setHomeworkOpen(false); loadDetail(); }} />
      )}
    </div>
  );
};

export default CollaborationHub;
